import type {
  ResolvedTheme,
  WidgetBrand,
  WidgetDefinition,
  WidgetTenant,
} from "./types";

// Kept in sync with packages/shared/src/schemas.ts — RADIUS_MAP + FONT_MAP.
// Duplicated (not imported) so the widget bundle stays independent of the
// admin workspace and can ship as a tiny CDN artefact.

const RADIUS_MAP = {
  sharp: { sm: "0px", md: "2px", lg: "4px" },
  soft: { sm: "6px", md: "10px", lg: "16px" },
  rounded: { sm: "8px", md: "12px", lg: "20px" },
  pill: { sm: "12px", md: "18px", lg: "28px" },
} as const;

const FONT_MAP = {
  vertu: {
    display: "'Fraunces', Georgia, serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  heritage: {
    display: "'Cormorant Garamond', 'Times New Roman', serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  modern: {
    display: "'Inter', -apple-system, sans-serif",
    body: "'Inter', -apple-system, sans-serif",
  },
  editorial: {
    display: "'Playfair Display', Georgia, serif",
    body: "'Inter', -apple-system, sans-serif",
  },
  contemporary: {
    display: "'Fraunces', Georgia, serif",
    body: "'Manrope', -apple-system, sans-serif",
  },
  minimal: {
    display: "'DM Sans', -apple-system, sans-serif",
    body: "'DM Sans', -apple-system, sans-serif",
  },
  refined: {
    display: "'Roboto Serif', Georgia, serif",
    body: "'Inter Tight', -apple-system, sans-serif",
  },
} as const;

// Font-size scale presets. Applied as a multiplier on the widget's base sizes
// (see the `--vt-btn-scale` / `--vt-text-scale` rules in styles.css) so nothing
// hard-codes a px size a tenant could overflow.
const SIZE_MAP = {
  small: 0.9,
  default: 1,
  large: 1.12,
  xlarge: 1.28,
} as const;

type RadiusKey = keyof typeof RADIUS_MAP;
type FontKey = keyof typeof FONT_MAP;
type SizeKey = keyof typeof SIZE_MAP;

interface WidgetThemeOverrides {
  primaryColor?: string;
  accentColor?: string;
  accentLight?: string;
  accentSoft?: string;
  creamColor?: string;
  warmCreamColor?: string;
  warmCreamDark?: string;
  charcoal?: string;
  slateMuted?: string;
  slateLight?: string;
  border?: string;
  borderLight?: string;
  success?: string;
  danger?: string;
  // CTA colours — when unset we default to Vertu purple / white so the
  // primary action is always recognisable as a button.
  ctaBackground?: string;
  ctaText?: string;
  radius?: RadiusKey;
  fonts?: FontKey;
  // Tenant font-size presets — buttons vs everything-else (titles/body/desc).
  buttonTextSize?: SizeKey;
  bodyTextSize?: SizeKey;
  heroOverlayOpacity?: number;
  headerFill?: "gradient" | "solid";
  venueNameStyle?: "display" | "caps";
  hoursPillStyle?: "status" | "brand";
}

// Platform defaults. These should match the prototype palette / radii
// since the prototype is the canonical "production" look. Tenants who
// want a different look override via tenant columns + widget.theme jsonb.
const DEFAULTS: ResolvedTheme = {
  primaryColor: "#1A1F51",
  accentColor: "#B8923E",
  accentLight: "#D4AF5E",
  accentSoft: "#F5EBD6",
  creamColor: "#FFFCF8",
  warmCreamColor: "#F7F3ED",
  warmCreamDark: "#EFE9DF",
  charcoal: "#2C2C2C",
  slateMuted: "#5A5A6E",
  slateLight: "#8A8A9E",
  border: "#E8E2D8",
  borderLight: "#F0EBE3",
  success: "#2D7D46",
  danger: "#B83A3A",
  white: "#FFFFFF",
  ctaBackground: "#B8923E",
  ctaText: "#FFFFFF",
  radiusSm: "8px",
  radiusMd: "12px",
  radiusLg: "16px",
  fontDisplay: FONT_MAP.heritage.display,
  fontBody: FONT_MAP.heritage.body,
  buttonScale: 1,
  textScale: 1,
};

/**
 * Resolve the theme for the current render context.
 *
 * Precedence (later overrides earlier):
 *   1. Platform defaults
 *   2. Tenant colours
 *   3. Brand colours (if a brand is active for the current tab)
 *   4. Widget-level theme overrides (jsonb → structured shape)
 */
export function resolveTheme(
  tenant: WidgetTenant,
  widget: WidgetDefinition,
  activeBrand: WidgetBrand | null,
): ResolvedTheme {
  const theme = { ...DEFAULTS };

  if (tenant.primary_color) theme.primaryColor = tenant.primary_color;
  if (tenant.accent_color) theme.accentColor = tenant.accent_color;

  if (activeBrand?.primary_color) theme.primaryColor = activeBrand.primary_color;
  if (activeBrand?.accent_color) theme.accentColor = activeBrand.accent_color;

  const overrides = (widget.theme ?? {}) as WidgetThemeOverrides;

  if (overrides.primaryColor) theme.primaryColor = overrides.primaryColor;
  if (overrides.accentColor) theme.accentColor = overrides.accentColor;
  if (overrides.accentLight) theme.accentLight = overrides.accentLight;
  if (overrides.accentSoft) theme.accentSoft = overrides.accentSoft;
  if (overrides.creamColor) theme.creamColor = overrides.creamColor;
  if (overrides.warmCreamColor) theme.warmCreamColor = overrides.warmCreamColor;
  if (overrides.warmCreamDark) theme.warmCreamDark = overrides.warmCreamDark;
  if (overrides.charcoal) theme.charcoal = overrides.charcoal;
  if (overrides.slateMuted) theme.slateMuted = overrides.slateMuted;
  if (overrides.slateLight) theme.slateLight = overrides.slateLight;
  if (overrides.border) theme.border = overrides.border;
  if (overrides.borderLight) theme.borderLight = overrides.borderLight;
  if (overrides.success) theme.success = overrides.success;
  if (overrides.danger) theme.danger = overrides.danger;
  if (overrides.ctaBackground) theme.ctaBackground = overrides.ctaBackground;
  if (overrides.ctaText) theme.ctaText = overrides.ctaText;

  if (overrides.radius && RADIUS_MAP[overrides.radius]) {
    const r = RADIUS_MAP[overrides.radius];
    theme.radiusSm = r.sm;
    theme.radiusMd = r.md;
    theme.radiusLg = r.lg;
  }

  if (overrides.fonts && FONT_MAP[overrides.fonts]) {
    theme.fontDisplay = FONT_MAP[overrides.fonts].display;
    theme.fontBody = FONT_MAP[overrides.fonts].body;
  }

  if (overrides.buttonTextSize && SIZE_MAP[overrides.buttonTextSize]) {
    theme.buttonScale = SIZE_MAP[overrides.buttonTextSize];
  }
  if (overrides.bodyTextSize && SIZE_MAP[overrides.bodyTextSize]) {
    theme.textScale = SIZE_MAP[overrides.bodyTextSize];
  }

  return theme;
}

/**
 * The widget root's style switches (data attributes styles.css reads), from
 * the widget theme. Only a non-default choice is emitted, so an untouched
 * widget renders exactly as before. Kept in sync with HEADER_FILLS /
 * VENUE_NAME_STYLES / HOURS_PILL_STYLES in packages/shared/src/schemas.ts.
 */
export function themeDataAttrs(widget: WidgetDefinition): Record<string, string> {
  const t = (widget.theme ?? {}) as WidgetThemeOverrides;
  const attrs: Record<string, string> = {};
  if (t.headerFill === "solid") attrs["data-vt-header-fill"] = "solid";
  if (t.venueNameStyle === "caps") attrs["data-vt-venue-name"] = "caps";
  if (t.hoursPillStyle === "brand") attrs["data-vt-hours-pill"] = "brand";
  return attrs;
}

/**
 * The widget's own CTA colours for the action buttons (Choose, Add to visit,
 * Complete booking, inline CTAs), only when the widget sets them: --vt-cta-bg
 * always carries a value (the platform gold when unset), so those buttons
 * read --vt-action-* instead and keep the accent for every widget without
 * a CTA colour of its own.
 */
export function ctaColorVars(widget: WidgetDefinition): Record<string, string> {
  const t = (widget.theme ?? {}) as WidgetThemeOverrides;
  const vars: Record<string, string> = {};
  if (t.ctaBackground) vars["--vt-action-bg"] = t.ctaBackground;
  if (t.ctaText) vars["--vt-action-fg"] = t.ctaText;
  return vars;
}

/** The dimmed page behind the checkout drawer. */
export const BACKDROP = "rgba(26, 31, 81, 0.4)";

/**
 * Produce a CSS variable object for inlining on the widget root.
 *
 * Derivable tokens (accent-light, accent-soft, cream, warm-cream-dark,
 * slate-light, border-light) are only emitted when a tenant / brand /
 * widget override explicitly set them away from the platform default.
 * When they ARE still the default, we skip emitting — that lets the
 * `@supports color-mix` block at the top of styles.css derive them from
 * the tenant's actual --vt-accent instead of leaking Vertu purple into
 * a gold/navy tenant like Laborie.
 */
export function themeToCssVars(theme: ResolvedTheme): Record<string, string> {
  const vars: Record<string, string> = {
    "--vt-primary": theme.primaryColor,
    "--vt-accent": theme.accentColor,
    "--vt-warm-cream": theme.warmCreamColor,
    "--vt-charcoal": theme.charcoal,
    "--vt-slate-muted": theme.slateMuted,
    "--vt-border": theme.border,
    "--vt-success": theme.success,
    "--vt-danger": theme.danger,
    "--vt-white": theme.white,
    "--vt-cta-bg": theme.ctaBackground,
    "--vt-cta-fg": theme.ctaText,
    "--vt-radius-sm": theme.radiusSm,
    "--vt-radius-md": theme.radiusMd,
    "--vt-radius-lg": theme.radiusLg,
    "--vt-font-display": theme.fontDisplay,
    "--vt-font-body": theme.fontBody,
    "--vt-btn-scale": String(theme.buttonScale),
    "--vt-text-scale": String(theme.textScale),
    // The drawer's dimmed page behind it (.vt-cart-backdrop): a token, so no
    // colour is written into the stylesheet itself. Today's value.
    "--vt-backdrop": BACKDROP,
  };
  if (theme.accentLight !== DEFAULTS.accentLight)
    vars["--vt-accent-light"] = theme.accentLight;
  if (theme.accentSoft !== DEFAULTS.accentSoft)
    vars["--vt-accent-soft"] = theme.accentSoft;
  if (theme.creamColor !== DEFAULTS.creamColor)
    vars["--vt-cream"] = theme.creamColor;
  if (theme.warmCreamDark !== DEFAULTS.warmCreamDark)
    vars["--vt-warm-cream-dark"] = theme.warmCreamDark;
  if (theme.slateLight !== DEFAULTS.slateLight)
    vars["--vt-slate-light"] = theme.slateLight;
  if (theme.borderLight !== DEFAULTS.borderLight)
    vars["--vt-border-light"] = theme.borderLight;
  return vars;
}

/**
 * Inline CSS vars that re-colour a service panel's hero HEADER to that
 * service's OWN brand.
 *
 * Why this exists: the widget-root theme is resolved once from a single
 * widget-level `activeBrand` (embed slug / widget.brand_id). In a multi-brand
 * widget that brand is null, so every tab's hero falls back to the tenant
 * palette — a brand's configured colours never reach its own panel header.
 * Scoping the active tab's brand colours to its `.vt-hero` (only) fixes that
 * without re-theming the whole widget.
 *
 * Precedence is preserved: a widget-level primary/accent override still wins
 * over the brand (matches resolveTheme step 4 > step 3), so we skip a channel
 * the widget explicitly set.
 */
export function heroBrandVars(
  brand: WidgetBrand | null,
  widget: WidgetDefinition,
): Record<string, string> {
  const over = (widget.theme ?? {}) as {
    primaryColor?: string;
    accentColor?: string;
  };
  const vars: Record<string, string> = {};
  if (!over.primaryColor && brand?.primary_color)
    vars["--vt-primary"] = brand.primary_color;
  if (!over.accentColor && brand?.accent_color)
    vars["--vt-accent"] = brand.accent_color;
  // Header button — coloured per brand. Scoped to the panel hero (or whole
  // panel) by the caller; the CSS falls back to the shared CTA colour when
  // unset (var(--vt-hero-cta-bg, var(--vt-cta-bg, …))).
  if (brand?.hero_cta_color)
    vars["--vt-hero-cta-bg"] = brand.hero_cta_color;
  if (brand?.hero_cta_text_color)
    vars["--vt-hero-cta-fg"] = brand.hero_cta_text_color;
  return vars;
}
